import React, { useEffect } from 'react';
import { FormLabelHookForm, Group, Row } from '@common/components/form';
import { TextInputHookForm } from '@common/components/form/inputs/text';
import { Toggle } from '@common/components/form/inputs/toggle';
import { QuestionIcon } from '@modules/performance/components/question-icon/question-icon';
import { PerformanceQuestionType } from '@modules/performance/definitions';
import { useTranslation } from 'react-i18next';
import { PerformanceFormComponent } from '@modules/performance/types';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Checkbox } from '@common/components/form/inputs/checkbox';
import { usePerformanceStore } from '@modules/performance/store/store';
import Spinner from '@common/components/spinner';
import ContentModuleEditor from '@modules/performance/components/content-module-editor/content-module-editor';
import Icon from '@common/components/icon';
import { EvaluationFormTypes } from '@common/definitions';

type OwnProps = {
  component?: PerformanceFormComponent;
  addComponent?: any;
  match: any;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  deleteComponent?: any;
  isSubmitting?: boolean;
};

export const QuestionContainer = ({
  component,
  control,
  setValue,
  watch,
  isSubmitting,
}: OwnProps) => {
  const { t } = useTranslation();
  const { categories, drafts, updateDraft, form } = usePerformanceStore();

  useEffect(() => {
    if (!component?.id) return;

    const draft = drafts?.[component.id] || {};

    const managerValue =
      draft.manager ?? component?.settings?.visibility?.manager ?? false;
    const employeeValue =
      draft.employee ?? component?.settings?.visibility?.employee ?? false;
    const defaultEmployee =
      !managerValue && !employeeValue ? true : employeeValue;

    setValue('text', draft.text ?? component?.settings?.text ?? '');
    setValue(
      'description',
      draft.description ?? component?.settings?.description ?? ''
    );
    setValue('manager', managerValue);
    setValue('employee', defaultEmployee);
    setValue(
      'answer_required',
      draft.answer_required ?? component?.settings?.answer_required ?? false
    );
    setValue(
      'category_ids',
      draft.category_ids ?? component?.settings?.category_ids ?? []
    );
    setValue(
      'answer_visible',
      draft.answer_visible ??
        component?.settings?.visibility?.answer_visible ??
        false
    );

    if (component?.type === PerformanceQuestionType.CONTENT_MODULE) {
      const contentComponents = Array.isArray(component.settings)
        ? component.settings
        : component.settings?.content_components || [];
      setValue('content_components', contentComponents);
    }

    if (component?.type === PerformanceQuestionType.TALENT_REVIEW) {
      setValue(
        'impact_of_loss',
        draft.impact_of_loss ?? component?.settings?.impact_of_loss ?? false
      );
      setValue(
        'retention_risk',
        draft.retention_risk ?? component?.settings?.retention_risk ?? false
      );
    }
  }, [component?.id, setValue]);

  if (isSubmitting) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-[300px]">
        <Spinner size="large" centered />
      </div>
    );
  }

  return (
    <>
      <div className="tw-text-xs tw-font-semibold tw-text-gray-muted tw-uppercase">
        {t('performance:manage_block')}
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
          <QuestionIcon type={component?.type || ''} />
          <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
            <div className="tw-text-base tw-font-medium tw-text-gray-textDark">
              {t('performance:question_type_title', {
                context: component?.type,
              })}
            </div>
            {/* <QuestionDropdown
            key={component?.type}
            toggle={
              <div className="tw-flex tw-flex-row tw-items-center tw-gap-3 tw-cursor-pointer">
                <QuestionIcon type={component?.type || ''} />
                <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                  <div className="tw-text-base tw-font-medium tw-text-gray-textDark">
                    {t('performance:question_type_title', {
                      context: component?.type,
                    })}
                  </div>
                  <Icon
                    type="arrow_down_default"
                    className="tw-text-gray-muted !tw-w-4 !tw-h-4"
                  />
                </div>
              </div>
            }
            enabledComponents={[
              PerformanceQuestionType.OPEN_QUESTION,
              PerformanceQuestionType.COMPETENCIES,
            ]}
            onSelect={() => {}}
          /> */}
          </div>
        </div>
        {component?.type === PerformanceQuestionType.COMPETENCIES && (
          <div className="tw-text-sm-plus tw-text-gray-muted tw-font-normal">
            {t('performance:competencies_description')}
          </div>
        )}
      </div>

      {/* Question Form */}
      {component && component.type === PerformanceQuestionType.OPEN_QUESTION && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Row>
            <Group>
              <FormLabelHookForm
                required
                control={control}
                name="text"
                text={t('performance:enter_question')}
                className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
              />
              <TextInputHookForm
                name="text"
                control={control}
                placeholder={t('performance:enter_question')}
                className="!tw-shadow-input"
                onChange={(e: any) => {
                  setValue('text', e?.target?.value ?? e);
                  if (component?.id)
                    updateDraft(component.id, { text: e?.target?.value ?? e });
                }}
              />
            </Group>
          </Row>

          <Row>
            <Group>
              <FormLabelHookForm
                required
                control={control}
                name="description"
                text={t('performance:description')}
                className="!tw-text-[13px] !tw-font-inter !tw-font-normal !tw-text-gray-text"
              />
              <TextInputHookForm
                name="description"
                control={control}
                placeholder={t('performance:enter_description')}
                className="!tw-shadow-input"
                onChange={(e: any) => {
                  setValue('description', e?.target?.value ?? e);
                  if (component?.id)
                    updateDraft(component.id, {
                      description: e?.target?.value ?? e,
                    });
                }}
              />
            </Group>
          </Row>

          {form.type === EvaluationFormTypes.MANAGER_EMPLOYEE && (
            <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow" />
          )}

          {form.type === EvaluationFormTypes.MANAGER_EMPLOYEE && (
            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-text-sm-plus tw-font-normal tw-text-gray-text">
                {t('performance:who_fills_it_in')}
              </div>
              <div className="tw-flex tw-flex-col tw-gap-2">
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <div className="tw-flex tw-flex-row tw-gap-2">
                    <Checkbox
                      className="!tw-my-0 !tw-font-inter !tw-font-medium !tw-text-gray-textDark"
                      label={t('performance:manager')}
                      value={watch('manager')}
                      onChange={(value) => {
                        setValue('manager', value);
                        if (component?.id)
                          updateDraft(component.id, { manager: value });
                      }}
                    />
                  </div>
                  {watch('manager') && (
                    <div className="tw-flex tw-flex-row tw-gap-2">
                      <div className="tw-w-[1px] tw-h-8 tw-flex tw-flex-col tw-bg-gray-border tw-ml-[6px]" />
                      <div className="tw-flex tw-flex-row tw-gap-2 tw-ml-3">
                        <Toggle
                          value={watch('answer_visible')}
                          onChange={(value) => {
                            setValue('answer_visible', value);
                            if (component?.id)
                              updateDraft(component.id, {
                                answer_visible: value,
                              });
                          }}
                          label={t('performance:answer_visible_for_employee')}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="tw-flex tw-flex-row tw-gap-2">
                  <Checkbox
                    className="!tw-my-0 !tw-font-inter !tw-font-medium !tw-text-gray-textDark"
                    label={t('performance:only_employee')}
                    value={watch('employee')}
                    onChange={(value) => {
                      setValue('employee', value);
                      if (component?.id)
                        updateDraft(component.id, { employee: value });
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {form.type === EvaluationFormTypes.MANAGER_EMPLOYEE && (
            <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow" />
          )}

          <Row>
            <Group>
              <Toggle
                value={watch('answer_required')}
                onChange={(value) => {
                  setValue('answer_required', value);
                  if (component?.id)
                    updateDraft(component.id, { answer_required: value });
                }}
                label={t('performance:answer_is_required')}
              />
            </Group>
          </Row>
        </div>
      )}

      {component?.type === PerformanceQuestionType.COMPETENCIES && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          {(() => {
            const options = Array.isArray(categories)
              ? (categories as any[]).map((c: any) => ({
                  id: String(c.id),
                  name: c.name,
                }))
              : [];

            const selectedIds: string[] = watch('category_ids') || [];
            const allSelected =
              options.length > 0 && selectedIds.length === options.length;

            return (
              <>
                <div className="tw-bg-gray-custom-50 tw-rounded-md tw-px-4 tw-py-3">
                  <Checkbox
                    className="!tw-my-0 !tw-font-inter !tw-font-medium !tw-text-gray-textDark"
                    label={t('performance:select_all')}
                    value={allSelected}
                    onChange={(checked) => {
                      setValue(
                        'category_ids',
                        checked ? options.map((o) => o.id) : []
                      );
                      if (component?.id)
                        updateDraft(component.id, {
                          category_ids: checked ? options.map((o) => o.id) : [],
                        });
                    }}
                  />
                </div>

                <div className="tw-flex tw-flex-col tw-overflow-hidden tw-bg-white">
                  {options.map((option, index) => (
                    <div key={option.id} className="tw-px-4">
                      <Checkbox
                        className="!tw-my-0 !tw-font-inter !tw-font-medium !tw-text-gray-textDark"
                        label={option.name}
                        value={selectedIds.includes(option.id)}
                        onChange={(checked) => {
                          if (checked) {
                            setValue('category_ids', [
                              ...selectedIds,
                              option.id,
                            ]);
                            if (component?.id)
                              updateDraft(component.id, {
                                category_ids: [...selectedIds, option.id],
                              });
                          } else {
                            setValue(
                              'category_ids',
                              selectedIds.filter((item) => item !== option.id)
                            );
                            if (component?.id)
                              updateDraft(component.id, {
                                category_ids: selectedIds.filter(
                                  (item) => item !== option.id
                                ),
                              });
                          }
                        }}
                      />
                      {index < options.length - 1 && (
                        <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow tw-mt-4 tw-mb-4" />
                      )}
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {component?.type === PerformanceQuestionType.CONTENT_MODULE &&
        component && (
          <ContentModuleEditor
            component={component as PerformanceFormComponent}
          />
        )}

      {component?.type === PerformanceQuestionType.TALENT_REVIEW && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-text-sm-plus tw-font-normal tw-text-gray-text">
            {t('performance:talent_review_description')}
          </div>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow" />
            <div className="tw-flex tw-flex-row tw-gap-1 tw-items-center">
              <Toggle
                value={watch('retention_risk')}
                onChange={(value) => {
                  setValue('retention_risk', value);
                  if (component?.id)
                    updateDraft(component.id, {
                      retention_risk: value,
                    });
                }}
                label={t('performance:ask_retention_risk_question')}
              />
              <Icon
                type="info"
                color="gray"
                customClassName="!tw-w-4 !tw-h-4"
              />
            </div>
            <div className="tw-flex tw-flex-row tw-gap-1 tw-items-center">
              <Toggle
                value={watch('impact_of_loss')}
                onChange={(value) => {
                  setValue('impact_of_loss', value);
                  if (component?.id)
                    updateDraft(component.id, {
                      impact_of_loss: value,
                    });
                }}
                label={t('performance:ask_impact_of_loss_question')}
              />
              <Icon
                type="info"
                color="gray"
                customClassName="!tw-w-4 !tw-h-4"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
